import React, { useMemo, type JSX } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { selectSelectedDealId } from "./DealsTableSlice_Store";
import { openDocumentPreview } from "./DocumentPreviewSlice_Store";
import { setLayoutMode } from "./LayoutSlice_Store";
import { canPreviewDocument } from "./services/documentPreviewService";
import type { Document } from "./types/redux";

/**
 * "Docs" tab content for the RHS deal panel.
 * Reads documents from the cross-turn documentRegistry (not the deal detail
 * API, which doesn't return documents) and filters by dealId — same approach
 * as the existing DealDetailsModal in DealsTable_Component.tsx.
 */
export default function DealDocsPanel(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const dealId = useAppSelector(selectSelectedDealId);

  // documentRegistry accumulates across turns so docs from earlier searches
  // are still available when the user clicks a deal from a prior message.
  const registry = useAppSelector(
    (state) => (state.resultsSlice?.documentRegistry || [])
  );

  const documents = useMemo(() => {
    if (!dealId) return [];
    return registry.filter((doc) => doc.dealId === dealId);
  }, [registry, dealId]);

  if (!dealId) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header with count */}
      <div className="px-4 py-3 border-b border-gray-200 shrink-0">
        <h3 className="text-sm font-medium text-gray-500">
          {documents.length} {documents.length === 1 ? "document" : "documents"}
        </h3>
      </div>

      {/* Document list */}
      <div className="flex-1 overflow-y-auto">
        {documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No documents available for this deal.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {documents.map((doc) => {
              const name = doc.originalFilename || doc.documentName || "Untitled Document";
              const previewable = canPreviewDocument(doc);

              return (
                <li
                  key={doc.docId || doc.documentId}
                  className={`px-4 py-3 flex items-center gap-2 transition-colors ${
                    previewable
                      ? "hover:bg-gray-50 cursor-pointer"
                      : "opacity-60 cursor-default"
                  }`}
                  onClick={() => {
                    if (!previewable) return;
                    dispatch(openDocumentPreview({ ...doc, docId: doc.docId || doc.documentId }));
                    dispatch(setLayoutMode("workspace"));
                  }}
                >
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-900 truncate">{name}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
